import {FC, useMemo} from 'react'
import {InvoiceFieldRuleV79, InvoiceRulesV79, InvoiceRuleStatus} from '../core/invoiceRulesV79'
import {TEHRAN_TIME_ZONE} from '../core/tehranTime'
import {FieldHelpLabel} from '../../../../../../_metronic/helpers/components/FieldHelp'

type Props = {
  rules?: InvoiceRulesV79
  loading: boolean
  error?: string
  itemCount: number
  isArticle9: boolean
  registeredDateTime?: number
}

const statusText: Record<InvoiceRuleStatus, string> = {
  Required: 'اجباری',
  ConditionallyRequired: 'مشروط',
  Optional: 'اختیاری',
  OutOfPattern: 'خارج از الگو',
  ReadOnly: 'فقط‌خواندنی',
  Calculated: 'محاسبه خودکار',
  InheritedFromReference: 'دریافت‌شده از مرجع',
}

const RuleList: FC<{title: string; fields: InvoiceFieldRuleV79[]; tone: string}> = ({
  title,
  fields,
  tone,
}) => {
  if (!fields.length) return null
  return (
    <div className='col-12 col-md-6 col-xl-3 mb-4'>
      <section className='border border-gray-300 rounded bg-light p-4 h-100'>
        <div className='d-flex align-items-center justify-content-between gap-2 mb-3'>
          <h6 className={`text-${tone} mb-0`}>
            <FieldHelpLabel label={title} />
          </h6>
          <span className={`badge badge-light-${tone}`}>{fields.length} فیلد</span>
        </div>
        <ul
          className='list-unstyled d-flex flex-column gap-3 mb-0 pe-1 overflow-auto'
          style={{maxHeight: '32rem'}}
        >
          {fields.map((field) => (
            <li
              key={`${field.section}-${field.jsonName}`}
              className='border-bottom border-gray-300 pb-3'
            >
              <div className='d-flex flex-wrap align-items-center gap-2'>
                <span className='fw-bold text-gray-900'>{field.titleFa}</span>
                <code className='badge badge-light-primary text-primary fs-8' dir='ltr'>
                  {field.jsonName}
                </code>
              </div>
              <div className='text-gray-700 fs-8 lh-lg mt-1'>
                {field.hint || field.errorMessage}
                {field.maxLength ? ` حداکثر ${field.maxLength} کاراکتر.` : ''}
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  )
}

const InvoiceRulesV79Panel: FC<Props> = ({
  rules,
  loading,
  error,
  itemCount,
  isArticle9,
  registeredDateTime,
}) => {
  const grouped = useMemo(() => {
    const fields = rules?.fields || []
    return {
      required: fields.filter((field) =>
        ['Required', 'ConditionallyRequired'].includes(field.status)
      ),
      optional: fields.filter((field) => field.status === 'Optional'),
      outOfPattern: fields.filter((field) => field.status === 'OutOfPattern'),
      managed: fields.filter((field) =>
        ['ReadOnly', 'Calculated', 'InheritedFromReference'].includes(field.status)
      ),
    }
  }, [rules])

  if (!rules?.enabled && !loading && !error) return null

  const maximumRows = rules?.rowPolicy.maximumRows
  const registeredText = registeredDateTime
    ? new Date(registeredDateTime).toLocaleString('fa-IR', {timeZone: TEHRAN_TIME_ZONE})
    : undefined

  return (
    <details className='mp-invoice-rules-panel text-gray-900' data-bs-theme='light' dir='rtl'>
      <summary className='mp-invoice-rules-panel__summary cursor-pointer'>
        <span className='mp-invoice-rules-panel__title'>
          <i className='las la-clipboard-list' aria-hidden='true'></i>
          <FieldHelpLabel label='قوانین این صورتحساب' />
        </span>
        <span className='mp-invoice-rules-panel__meta'>
          {rules?.instructionVersion && (
            <span className='badge badge-light-info'>{rules.instructionVersion}</span>
          )}
          <i className='las la-angle-down mp-invoice-rules-panel__chevron' aria-hidden='true'></i>
        </span>
      </summary>
      <div className='mp-invoice-rules-panel__body'>
        {loading && <div className='text-gray-600'>در حال دریافت قوانین…</div>}
        {error && <div className='alert alert-warning p-3'>{error}</div>}
        {rules && (
          <>
            <div className='d-flex flex-wrap gap-2 mb-4'>
              <span
                className={`badge ${isArticle9 ? 'badge-light-warning' : 'badge-light-success'}`}
              >
                {isArticle9 ? 'صورتحساب موضوع ماده ۹' : 'صورتحساب عادی (ماده ۹ غیرفعال)'}
              </span>
              <span className='badge badge-light-primary'>
                تعداد قلم: {itemCount}
                {maximumRows ? ` از ${maximumRows}` : ''}
              </span>
              <span className='badge badge-light-info'>منبع قواعد: PDF تأییدشده</span>
              {!rules.rowPolicy.bodyEditable && (
                <span className='badge badge-light-secondary'>بدنه فقط‌خواندنی</span>
              )}
              {!rules.rowPolicy.newItemIdsAllowed && (
                <span className='badge badge-light-secondary'>قلم جدید مجاز نیست</span>
              )}
              {rules.rowPolicy.amountMustEqualOne && (
                <span className='badge badge-light-danger'>مقدار هر ردیف باید ۱ باشد</span>
              )}
            </div>
            {registeredText && (
              <p className='text-gray-700'>تاریخ و زمان ثبت صورتحساب: {registeredText}</p>
            )}
            {rules.rowPolicy.message && (
              <div className='alert alert-info p-3'>{rules.rowPolicy.message}</div>
            )}
            {!!rules.errors.length && (
              <div className='alert alert-danger p-3'>
                {rules.errors.map((message) => (
                  <div key={message}>{message}</div>
                ))}
              </div>
            )}
            <div className='row'>
              <RuleList title='فیلدهای اجباری و مشروط' fields={grouped.required} tone='danger' />
              <RuleList title='فیلدهای اختیاری' fields={grouped.optional} tone='primary' />
              <RuleList
                title='فیلدهای خارج از الگو'
                fields={grouped.outOfPattern}
                tone='secondary'
              />
              <RuleList title='فیلدهای سیستمی و ارجاعی' fields={grouped.managed} tone='info' />
            </div>
            <div className='alert alert-light-info p-3 mb-0'>
              <strong>قاعده ارسال صورتحساب (insr): </strong>
              کاربر فقط ماده ۹ و تاریخ ثبت را مشخص می‌کند؛ پس از اعتبارسنجی نهایی سرور، مقدار ۱ برای
              صورتحساب معتبر ماده ۹ تولید می‌شود و در صورتحساب عادی خارج از الگو باقی می‌ماند.{' '}
              <strong>تاریخ ثبت (indati2m)</strong> نیز مستقل از تاریخ صدور و فقط برای ماده ۹ ارسال
              می‌شود.
            </div>
          </>
        )}
      </div>
    </details>
  )
}

export {InvoiceRulesV79Panel, statusText}
